// src/modules/app-views/fiscalConfig/pages/Document/tabs/NfeTab/modals/VoidNfeModal/index.jsx
// *******************************************************************************
// VOID NFCe MODAL - Inutilização de numeração NF-e
// *******************************************************************************
// Modal for voiding (inutilização) NF-e number ranges with SEFAZ
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useState } from 'react';
import { ConfigProvider } from 'antd';
import { createRoot } from 'react-dom/client';
import GenericModal from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import { darkTheme, lightTheme } from 'configs/appEngine';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import translateX from 'utils/translateX';
import voidNfeModalStep from './Step1';

// *******************************************************************************
// THEME WRAPPER
// *******************************************************************************
const ThemeWrapper = ({ children }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;
  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const VoidNfeModal = ({ show, setShow, afterClose, company, onSuccess }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [resultData, setResultData] = useState(null);
  const [showResult, setShowResult] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // MEMOS
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = useMemo(
    () => [voidNfeModalStep({ company, resultData, showResult })],
    [company, resultData, showResult]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form, setLoading }) => {
    // If showing result, just close the modal
    if (showResult) {
      return true;
    }

    const values = form?.getFieldsValue(true) || {};

    const companyId = company?._id || company?.entityId;
    const companySerie = company?.fiscalData?.nfe?.currentSerie || 1;

    // Simplified payload - backend fetches cnpj and ambiente from companyId
    const payload = {
      companyId,
      serie: companySerie,
      numero_inicial: values.numero_inicial,
      numero_final: values.numero_final,
      justificativa: values.justificativa,
    };

    setLoading(true);

    try {
      const response = await axiosService.post({
        url: API.FISCAL.NFE_VOID,
        data: payload,
      });

      console.log('[VoidNfeModal] Full response:', response);

      // Handle axiosService response format (can be array or object)
      let responseData;
      if (Array.isArray(response)) {
        // axiosService returns [data, summary] when returnAsObject is used
        responseData = response[0];
      } else {
        responseData = response;
      }

      console.log('[VoidNfeModal] Response data:', responseData);

      // Check success at root level (routerService wraps response)
      const isSuccess = responseData?.success === true;
      const hasData = responseData?.data?.success === true;

      console.log('[VoidNfeModal] Success check:', { isSuccess, hasData });

      if (isSuccess || hasData) {
        // Get data from either root or data property
        const successData = hasData ? responseData.data : responseData;

        // Store result data for display
        const result = {
          success: true,
          protocolNumber: successData.protocolNumber || successData.results?.[0]?.protocolNumber,
          operationId: successData.operationId,
          processed: successData.processed,
          results: successData.results,
          xml: successData.xml,
          xmlStorageUrl: successData.xmlStorageUrl,
          range: successData.results?.[0] || { start: values.numero_inicial, end: values.numero_final },
        };

        setResultData(result);
        setShowResult(true);

        // Callback for parent component
        onSuccess?.(result);

        // Don't close modal - show result instead
        return false;
      } else {
        // Get error details from response
        const errorDetails = responseData?.raw || responseData;

        // Build meaningful error message
        let errorMsg = translateX('void_nfe_failed');
        let errorCode = null;

        // Check for specific SEFAZ errors
        if (responseData?.errors?.[0]?.error) {
          errorMsg = responseData.errors[0].error;
          errorCode = responseData.errors[0].code;
        } else if (errorDetails?.motivo_status) {
          // NuvemFiscal rejection
          errorMsg = errorDetails.motivo_status;
          errorCode = errorDetails.codigo_status;
        } else if (errorDetails?.errors?.[0]?.message) {
          errorMsg = errorDetails.errors[0].message;
        } else if (errorDetails?.error?.message) {
          errorMsg = errorDetails.error.message;
        } else if (responseData?.message) {
          errorMsg = responseData.message;
        } else if (responseData?.error) {
          errorMsg = responseData.error;
        }

        console.log('[VoidNfeModal] Error details:', { responseData, errorDetails, errorMsg, errorCode });

        // Store error data for display in error view
        const errorResult = {
          success: false,
          error: errorMsg,
          code: errorCode,
          xml: responseData?.xml,
          xmlStorageUrl: responseData?.xmlStorageUrl,
          range: { start: values.numero_inicial, end: values.numero_final },
        };

        setResultData(errorResult);
        setShowResult(true);

        return false;
      }
    } catch (error) {
      console.error('[VoidNfeModal] Error:', error);

      const errorMsg =
        error?.response?.data?.errors?.[0]?.error ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        translateX('void_nfe_failed');

      appService.message({
        flag: 'e',
        text: errorMsg,
        duration: 5,
      });

      return false;
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      title={showResult ? 'void_nfe_result_title' : 'void_nfe_title'}
      steps={steps}
      onFinish={handleFinish}
      afterClose={afterClose}
      width={560}
      maskClosable={false}
      nextButtonText={showResult ? 'close' : 'void_numbers'}
    />
  );
};

// *******************************************************************************
// SHOW MODAL FUNCTION
// *******************************************************************************
const showVoidNfeModal = ({ company, onSuccess } = {}) => {
  const container = document.createElement('div');
  container.id = 'void-nfe-modal-container';
  document.body.appendChild(container);

  const root = createRoot(container);
  let isModalOpen = true;

  const renderModal = () => {
    root.render(
      <ThemeWrapper>
        <VoidNfeModal
          show={isModalOpen}
          setShow={handleClose}
          afterClose={handleAfterClose}
          company={company}
          onSuccess={onSuccess}
        />
      </ThemeWrapper>
    );
  };

  const handleClose = () => {
    isModalOpen = false;
    renderModal();
  };

  const handleAfterClose = () => {
    setTimeout(() => {
      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }, 0);
  };

  renderModal();
};

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default showVoidNfeModal;
export { VoidNfeModal };
